import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { describeAxiosError, formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { useTaxYear } from "@/context/TaxYearContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Save, Play, ArrowLeft, Plus, Trash2, Printer, RefreshCw, FileUp, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const AY = "2026-27";
const FY_START = "2025-04-01";
const FY_END = "2026-03-31";

const emptyHead = () => ({ label: "", auto: 0, manual: null, breakup: [] });

const DEFAULT_COMP = () => ({
  ay: AY,
  fy_start: FY_START,
  fy_end: FY_END,
  profile_snapshot: {
    taxpayer_type: "Individual",
    tax_regime: "New",
    pan: "",
    name: "",
    residential_status: "Resident",
    is_senior_citizen: false,
    is_super_senior_citizen: false,
    company_scheme: "",
    turnover_le_400cr: false,
  },
  salary: emptyHead(),
  house_property: emptyHead(),
  business_profession: emptyHead(),
  capital_gains: emptyHead(),
  other_sources: emptyHead(),
  // Head 5 detailed break-up — ALWAYS on (no toggle in UI)
  other_sources_detail: {
    interest_savings_bank: 0, interest_fd_rd: 0, interest_others: 0,
    dividend_domestic: 0, dividend_mutual_funds: 0, dividend_foreign: 0,
    family_pension: 0, rental_mach_furniture: 0,
    gifts_received: 0, winnings_lottery_games: 0, others_slab: 0,
    deduction_family_pension: null, deduction_others: 0, notes: "",
  },
  // Agriculture income (populated by /tax/agri-income push)
  agriculture_income: 0,
  // Per-head clubbing (populated by /tax/clubbing push)
  clubbing_by_head: {},
  net_profit_as_per_books: 0,
  additions: [],
  deductions: [],
  it_act_depreciation: 0,
  depreciation_as_per_books: 0,
  stcg_111a: 0,
  ltcg_112a: 0,
  ltcg_other: 0,
  stcg_normal: 0,
  setoff_current_year: 0,
  setoff_brought_forward: 0,
  chapter_via: [],
  rebate_87a_manual: null,
  surcharge_manual: null,
  tds: 0,
  tcs: 0,
  advance_tax: 0,
  self_assessment_tax: 0,
  tds_rows: [],
  interest_234a: null,          // null = auto-compute; number = manual override
  interest_234b: null,
  interest_234c: null,
  late_filing_fee_234f: null,   // Section 234F penalty — null = auto
  return_due_date: "2026-07-31",
  return_filing_date: "",
  computation_as_of_date: "",
  is_audit_case: false,
  presumptive_scheme: "",
  presumptive_turnover_digital: 0,
  presumptive_turnover_cash: 0,
  presumptive_declared_pct: null,
  presumptive_gross_receipts: 0,
  notes: "",
});

/** Small labelled number-input with manual/auto badge. */
function AmountInput({ label, value, onChange, testid, className = "", placeholder = "0" }) {
  return (
    <div className={`space-y-1 ${className}`}>
      <Label className="text-[11px] uppercase tracking-wider text-gray-600">{label}</Label>
      <Input
        type="number"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
        className="text-right font-mono h-9 tabular-nums"
        placeholder={placeholder}
        data-testid={testid}
      />
    </div>
  );
}

function SectionCard({ title, subtitle, right, children, testid }) {
  return (
    <div className="border border-gray-200 bg-white rounded" data-testid={testid}>
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-sm text-gray-900">{title}</h3>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        {right}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function ComputedCell({ label, value, highlight = false, amber = false }) {
  const cls = highlight
    ? "bg-emerald-100 text-emerald-900 border-emerald-300"
    : amber
    ? "bg-amber-100 text-amber-900 border-amber-300"
    : "bg-white border-gray-200 text-gray-800";
  return (
    <div className={`border rounded p-2 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-75">{label}</div>
      <div className="text-sm font-mono">₹ {formatINR(value || 0)}</div>
    </div>
  );
}

function RegimeCard({ label, data, highlight }) {
  return (
    <div
      className={`border rounded p-3 ${highlight ? "border-emerald-400 bg-emerald-50" : "border-gray-200 bg-white"}`}
      data-testid={`regime-card-${data.regime.toLowerCase()}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
          <div className="font-semibold text-sm">{data.regime} Regime</div>
        </div>
        {highlight && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-600 text-white uppercase tracking-wider" data-testid="regime-recommended">
            Recommended
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <div className="text-[10px] text-gray-500">Total Income</div>
          <div className="font-mono">₹ {formatINR(data.total_income)}</div>
        </div>
        <div>
          <div className="text-[10px] text-gray-500">Total Tax</div>
          <div className="font-mono font-medium">₹ {formatINR(data.tax_and_interest)}</div>
        </div>
      </div>
    </div>
  );
}

function Inner() {
  const { activeId } = useCompany();
  const { ay: selectedAy, meta: ayMeta } = useTaxYear();
  const navigate = useNavigate();
  const { tcid } = useParams();
  const isNew = !tcid || tcid === "new";

  const [comp, setComp] = useState(DEFAULT_COMP());
  const [computed, setComputed] = useState(null);
  const [reference, setReference] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  // AIS import — Feb 2026
  //   1. User clicks "Import from AIS" → uploads PDF
  //   2. Frontend POSTs /ais/extract/start (returns job_id in <500 ms — dodges
  //      Cloudflare's 100 s edge timeout for the multi-minute Gemini call)
  //   3. Frontend polls /ais/jobs/{job_id} every 1.5 s
  //   4. Once status=ready, show summary and let user click Apply
  //   5. Apply merges the extracted overlay onto `comp` — every field editable
  const [aisOpen, setAisOpen] = useState(false);
  const [aisFile, setAisFile] = useState(null);
  const [aisBusy, setAisBusy] = useState(false);
  const [aisResult, setAisResult] = useState(null);   // { extracted, summary, warnings }
  const [aisProgress, setAisProgress] = useState(null); // { message, pct }
  const aisPollRef = useRef(null);

  const stopAisPoll = () => {
    if (aisPollRef.current) {
      clearInterval(aisPollRef.current);
      aisPollRef.current = null;
    }
  };
  useEffect(() => stopAisPoll, []);

  const pollAisJob = (jobId) => new Promise((resolve, reject) => {
    stopAisPoll();
    let ticks = 0;
    const MAX_TICKS = 180; // 180 × 1.5s = 4.5 min ceiling
    aisPollRef.current = setInterval(async () => {
      ticks += 1;
      if (ticks > MAX_TICKS) {
        stopAisPoll();
        return reject(new Error("AI extraction is taking too long — please retry."));
      }
      try {
        const { data } = await api.get(`/companies/${activeId}/income-tax/ais/jobs/${jobId}`);
        setAisProgress({ message: data.message, pct: data.progress_pct });
        if (data.status === "ready") {
          stopAisPoll();
          resolve(data.result);
        } else if (data.status === "failed") {
          stopAisPoll();
          reject(data.error || { message: "Extraction failed" });
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          stopAisPoll();
          reject(new Error("Job disappeared — server may have restarted."));
        }
      }
    }, 1500);
  });

  const uploadAIS = async () => {
    if (!aisFile) { toast.error("Choose an AIS PDF first"); return; }
    setAisBusy(true);
    setAisResult(null);
    setAisProgress({ message: "Uploading …", pct: 5 });
    try {
      const fd = new FormData();
      fd.append("file", aisFile);
      // Kick off async job — <500 ms round-trip regardless of file size.
      const { data: startResp } = await api.post(
        `/companies/${activeId}/income-tax/ais/extract/start`, fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 },
      );
      // Poll to completion. Gemini typically finishes in 15-45 s.
      const result = await pollAisJob(startResp.job_id);
      setAisResult(result);
      toast.success("AIS parsed — review below and click Apply to fill the form");
    } catch (e) {
      const detail = e?.response?.data?.detail || e;
      const msg = detail?.message || describeAxiosError(e, "Could not extract AIS data");
      toast.error(msg);
    } finally {
      setAisBusy(false);
      setAisProgress(null);
      stopAisPoll();
    }
  };

  const applyAIS = () => {
    if (!aisResult?.extracted) return;
    // Merge extracted overlay onto current computation. For nested objects
    // like `other_sources_detail`, we shallow-merge so any fields the user
    // manually edited (e.g. family_pension, gifts) that AIS didn't cover
    // are preserved.
    setComp((c) => {
      const ext = aisResult.extracted;
      const merged = { ...c, ...ext };
      if (ext.other_sources_detail && c.other_sources_detail) {
        merged.other_sources_detail = { ...c.other_sources_detail, ...ext.other_sources_detail };
      }
      return merged;
    });
    setAisOpen(false);
    setAisFile(null);
    setAisResult(null);
    toast.success("AIS data applied — every field is editable before saving");
  };

  useEffect(() => {
    if (!activeId) return;
    (async () => {
      try {
        const [r, p] = await Promise.all([
          api.get(`/tax/reference`),
          api.get(`/companies/${activeId}/tax/profile`),
        ]);
        setReference(r.data);
        setProfile(p.data);
        if (!isNew) {
          const { data } = await api.get(`/companies/${activeId}/tax/computations/${tcid}`);
          const defaults = DEFAULT_COMP();
          // Head 5 detail is always shown — if the saved record has null, use defaults
          const osDetail = data.other_sources_detail || defaults.other_sources_detail;
          setComp({ ...defaults, ...data, other_sources_detail: osDetail });
          setComputed(data.computed || null);
        } else if (p.data) {
          setComp((c) => ({
            ...c,
            ay: selectedAy || c.ay,
            fy_start: ayMeta?.fy_start || c.fy_start,
            fy_end: ayMeta?.fy_end || c.fy_end,
            return_due_date: `${(selectedAy || c.ay).split("-")[0]}-07-31`,
            profile_snapshot: {
              taxpayer_type: p.data.taxpayer_type,
              tax_regime: p.data.tax_regime,
              pan: p.data.pan || "",
              name: p.data.name || "",
              residential_status: p.data.residential_status || "Resident",
              is_senior_citizen: !!p.data.is_senior_citizen,
              is_super_senior_citizen: !!p.data.is_super_senior_citizen,
              company_scheme: p.data.company_scheme || "",
              turnover_le_400cr: !!p.data.turnover_le_400cr,
            },
          }));
        }
      } catch (err) {
        toast.error(describeAxiosError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [activeId, tcid]);

  const preview = useCallback(async (payload) => {
    if (!activeId) return;
    setPreviewing(true);
    try {
      const { data } = await api.post(`/companies/${activeId}/tax/preview?compare_regimes=1`, payload);
      setComputed(data);
    } catch (err) {
      // silent — preview shouldn't nag
    } finally {
      setPreviewing(false);
    }
  }, [activeId]);

  // Auto-run preview 400ms after any change (debounced)
  useEffect(() => {
    if (loading) return;
    const t = setTimeout(() => preview(comp), 400);
    return () => clearTimeout(t);
  }, [comp, loading, preview]);

  const save = async () => {
    if (!profile) {
      toast.error("Please set up Taxpayer Profile first");
      navigate("/tax/profile");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post(`/companies/${activeId}/tax/computations`, comp);
        toast.success("Computation saved");
        navigate(`/tax/computation/${data.id}`);
      } else {
        const { data } = await api.put(`/companies/${activeId}/tax/computations/${tcid}`, comp);
        toast.success("Computation updated");
        // Reflect auto brought-forward set-off applied by the loss chain.
        setComp((c) => ({ ...c, setoff_brought_forward: data.setoff_brought_forward ?? c.setoff_brought_forward, setoff_bf_breakdown: data.setoff_bf_breakdown }));
      }
    } catch (err) {
      toast.error(describeAxiosError(err));
    } finally {
      setSaving(false);
    }
  };

  const addAdjustment = (kind) => {
    setComp((c) => ({ ...c, [kind]: [...(c[kind] || []), { label: "", amount: 0, notes: "" }] }));
  };

  const updateAdjustment = (kind, idx, patch) => {
    setComp((c) => {
      const arr = [...(c[kind] || [])];
      arr[idx] = { ...arr[idx], ...patch };
      return { ...c, [kind]: arr };
    });
  };

  const removeAdjustment = (kind, idx) => {
    setComp((c) => ({ ...c, [kind]: (c[kind] || []).filter((_, i) => i !== idx) }));
  };

  const addViaRow = (code) => {
    if (!code) return;
    if ((comp.chapter_via || []).some((r) => r.code === code)) return;
    const section = (reference.chapter_via_sections || []).find((s) => s.code === code);
    setComp((c) => ({ ...c, chapter_via: [...(c.chapter_via || []), { code, title: section?.title || code, claimed: 0, allowed: 0, notes: "" }] }));
  };

  const updateVia = (idx, patch) => {
    setComp((c) => {
      const arr = [...(c.chapter_via || [])];
      arr[idx] = { ...arr[idx], ...patch };
      return { ...c, chapter_via: arr };
    });
  };

  const removeVia = (idx) => {
    setComp((c) => ({ ...c, chapter_via: (c.chapter_via || []).filter((_, i) => i !== idx) }));
  };

  const availableViaOptions = useMemo(() => {
    if (!reference) return [];
    const regime = comp.profile_snapshot.tax_regime;
    const claimed = new Set((comp.chapter_via || []).map((r) => r.code));
    return (reference.chapter_via_sections || []).filter((s) => !claimed.has(s.code) && s.regimes.includes(regime));
  }, [reference, comp.profile_snapshot.tax_regime, comp.chapter_via]);

  if (loading || !reference) return <div className="p-6 text-sm text-gray-500">Loading…</div>;

  return (
    <>
      <PageHeader
        title={`Tax Computation — AY ${comp.ay || AY}`}
        subtitle={`${profile ? `${profile.taxpayer_type} · ${profile.tax_regime} Regime` : "No profile set"} · FY ${comp.fy_start || FY_START} → ${comp.fy_end || FY_END}`}
        actions={
          <>
            <Button variant="outline" onClick={() => navigate("/tax")} className="h-8" data-testid="btn-comp-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            {isNew && (
              <Button
                variant="outline"
                onClick={() => setAisOpen(true)}
                className="h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="btn-import-ais"
              >
                <FileUp className="w-4 h-4 mr-1" /> Import Data from AIS
              </Button>
            )}
            {!isNew && (
              <Button variant="outline" onClick={() => navigate(`/tax/computation/${tcid}/print`)} className="h-8" data-testid="btn-comp-print">
                <Printer className="w-4 h-4 mr-1" /> Print
              </Button>
            )}
            <Button onClick={save} disabled={saving} className="h-8 bg-blue-700 hover:bg-blue-800" data-testid="btn-comp-save">
              <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : (isNew ? "Save Computation" : "Update")}
            </Button>
          </>
        }
      />

      <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* LEFT: input columns */}
        <div className="lg:col-span-2 space-y-4">

          {/* Head 1: Salary */}
          <SectionCard title="Head 1 — Income from Salary" subtitle="Enter gross salary; standard deduction is applied automatically." testid="sec-salary">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <AmountInput label="Gross Salary (auto)" value={comp.salary.auto} onChange={(v) => setComp({ ...comp, salary: { ...comp.salary, auto: v } })} testid="input-salary-auto" />
              <AmountInput label="Manual override (optional)" value={comp.salary.manual ?? ""} onChange={(v) => setComp({ ...comp, salary: { ...comp.salary, manual: v || null } })} testid="input-salary-manual" />
            </div>
          </SectionCard>

          {/* Head 2: House Property */}
          <SectionCard title="Head 2 — Income from House Property" subtitle="Net rental income after 30% standard deduction & interest u/s 24." testid="sec-house">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <AmountInput label="Net Annual Value (after deductions)" value={comp.house_property.auto} onChange={(v) => setComp({ ...comp, house_property: { ...comp.house_property, auto: v } })} testid="input-house-auto" />
              <AmountInput label="Manual override" value={comp.house_property.manual ?? ""} onChange={(v) => setComp({ ...comp, house_property: { ...comp.house_property, manual: v || null } })} />
            </div>
          </SectionCard>

          {/* Head 3: Business / Profession */}
          <SectionCard
            title="Head 3 — Profits & Gains of Business or Profession"
            subtitle="Regular from-books computation OR opt for Presumptive Taxation (44AD/44ADA)."
            testid="sec-business"
          >
            {/* Presumptive Scheme Selector */}
            <div className="mb-4 border border-blue-200 bg-blue-50/40 rounded p-3" data-testid="presumptive-block">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="text-xs font-semibold uppercase tracking-wider text-blue-900">Presumptive Scheme</div>
                <div className="flex items-center gap-3 flex-wrap">
                  {["", "44AD", "44ADA"].map((s) => (
                    <label key={s || "none"} className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid={`presumptive-radio-${s || "none"}`}>
                      <input
                        type="radio"
                        checked={(comp.presumptive_scheme || "") === s}
                        onChange={() => setComp({ ...comp, presumptive_scheme: s })}
                      />
                      <span className="font-medium">{s === "" ? "None (regular from books)" : `Section ${s}`}</span>
                    </label>
                  ))}
                </div>
              </div>

              {comp.presumptive_scheme === "44AD" && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="presumptive-44ad-block">
                  <AmountInput label="Digital / Banking Receipts (@ 6%)" value={comp.presumptive_turnover_digital} onChange={(v) => setComp({ ...comp, presumptive_turnover_digital: v })} testid="input-44ad-digital" />
                  <AmountInput label="Cash Receipts (@ 8%)" value={comp.presumptive_turnover_cash} onChange={(v) => setComp({ ...comp, presumptive_turnover_cash: v })} testid="input-44ad-cash" />
                  <AmountInput label="Declared % (optional, must ≥ floor)" value={comp.presumptive_declared_pct ?? ""} onChange={(v) => setComp({ ...comp, presumptive_declared_pct: v || null })} testid="input-44ad-declared" />
                  <p className="col-span-full text-[11px] text-blue-800 leading-relaxed">
                    Section 44AD: Presumed income = 6% × Digital + 8% × Cash (or higher if declared). Cap ₹2 Cr (₹3 Cr if cash ≤ 5% of total). Net Profit as per Books, adjustments and IT-Act depreciation are IGNORED.
                  </p>
                </div>
              )}

              {comp.presumptive_scheme === "44ADA" && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="presumptive-44ada-block">
                  <AmountInput label="Gross Receipts (Profession)" value={comp.presumptive_gross_receipts} onChange={(v) => setComp({ ...comp, presumptive_gross_receipts: v })} testid="input-44ada-receipts" />
                  <AmountInput label="Declared % (optional, must ≥ 50%)" value={comp.presumptive_declared_pct ?? ""} onChange={(v) => setComp({ ...comp, presumptive_declared_pct: v || null })} testid="input-44ada-declared" />
                  <p className="col-span-full text-[11px] text-blue-800 leading-relaxed">
                    Section 44ADA: 50% of gross receipts presumed as income. Cap ₹75L (if cash receipts ≤ 5%) — otherwise ₹50L. For specified professions only (medical/legal/architecture/engineering/accountancy/technical consultancy/interior decoration/other notified).
                  </p>
                </div>
              )}

              {computed?.heads?.business_profession?.presumptive?.warning && (
                <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
                  ⚠️ {computed.heads.business_profession.presumptive.warning}
                </div>
              )}
            </div>

            {!comp.presumptive_scheme && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  <AmountInput label="Net Profit as per Books" value={comp.net_profit_as_per_books} onChange={(v) => setComp({ ...comp, net_profit_as_per_books: v })} testid="input-net-profit-books" />
                  <AmountInput label="Depreciation (as per Books) — for reference" value={comp.depreciation_as_per_books} onChange={(v) => setComp({ ...comp, depreciation_as_per_books: v })} />
                  <AmountInput label="IT-Act Depreciation (from Blocks)" value={comp.it_act_depreciation} onChange={(v) => setComp({ ...comp, it_act_depreciation: v })} testid="input-it-act-depreciation" />
                  <AmountInput label="Business Head — Manual override" value={comp.business_profession.manual ?? ""} onChange={(v) => setComp({ ...comp, business_profession: { ...comp.business_profession, manual: v || null } })} />
                </div>

                {/* Additions */}
                <div className="border-t border-gray-100 pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wider text-gray-700">Additions (added to Net Profit)</div>
                      <div className="text-[11px] text-gray-500">e.g. Depreciation as per Books, Personal expenses, Disallowed 40(a)(ia), 43B items…</div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => addAdjustment("additions")} className="h-7 text-xs" data-testid="btn-add-addition">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add
                    </Button>
                  </div>
                  {(comp.additions || []).length === 0 && <div className="text-xs text-gray-400 italic">No additions</div>}
                  {(comp.additions || []).map((r, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 mb-2 items-center">
                      <Input value={r.label} onChange={(e) => updateAdjustment("additions", idx, { label: e.target.value })} placeholder="e.g. Depreciation as per Books" className="col-span-6 h-8 text-xs" data-testid={`input-addition-label-${idx}`} />
                      <Input type="number" value={r.amount} onChange={(e) => updateAdjustment("additions", idx, { amount: Number(e.target.value) })} className="col-span-3 h-8 text-right font-mono text-xs" data-testid={`input-addition-amount-${idx}`} />
                      <Input value={r.notes} onChange={(e) => updateAdjustment("additions", idx, { notes: e.target.value })} placeholder="Notes" className="col-span-2 h-8 text-xs" />
                      <button onClick={() => removeAdjustment("additions", idx)} className="col-span-1 text-red-600 flex justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>

                {/* Deductions */}
                <div className="border-t border-gray-100 pt-3 mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wider text-gray-700">Deductions (subtracted from Net Profit)</div>
                      <div className="text-[11px] text-gray-500">e.g. Section 32AC investment allowance, exempt income already credited to P&L, etc.</div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => addAdjustment("deductions")} className="h-7 text-xs" data-testid="btn-add-deduction">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add
                    </Button>
                  </div>
                  {(comp.deductions || []).length === 0 && <div className="text-xs text-gray-400 italic">No deductions</div>}
                  {(comp.deductions || []).map((r, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 mb-2 items-center">
                      <Input value={r.label} onChange={(e) => updateAdjustment("deductions", idx, { label: e.target.value })} placeholder="e.g. Section 32AC allowance" className="col-span-6 h-8 text-xs" />
                      <Input type="number" value={r.amount} onChange={(e) => updateAdjustment("deductions", idx, { amount: Number(e.target.value) })} className="col-span-3 h-8 text-right font-mono text-xs" />
                      <Input value={r.notes} onChange={(e) => updateAdjustment("deductions", idx, { notes: e.target.value })} placeholder="Notes" className="col-span-2 h-8 text-xs" />
                      <button onClick={() => removeAdjustment("deductions", idx)} className="col-span-1 text-red-600 flex justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              </>
            )}

            {comp.presumptive_scheme && computed?.heads?.business_profession?.presumptive && (
              <div className="mt-2 grid grid-cols-2 gap-3 text-xs bg-gray-50 border border-gray-200 rounded p-3">
                <div className="text-gray-600">Presumptive Business Income</div>
                <div className="text-right font-mono font-semibold text-blue-900" data-testid="presumptive-computed">
                  ₹ {formatINR(computed.heads.business_profession.net || 0)}
                </div>
                {comp.presumptive_scheme === "44AD" && (
                  <>
                    <div className="text-gray-600">Turnover Total</div>
                    <div className="text-right font-mono">₹ {formatINR(computed.heads.business_profession.presumptive.turnover_total || 0)}</div>
                    <div className="text-gray-600">Cash % of Turnover</div>
                    <div className="text-right font-mono">{computed.heads.business_profession.presumptive.cash_pct}%</div>
                    <div className="text-gray-600">Applicable Cap</div>
                    <div className="text-right font-mono">₹ {formatINR(computed.heads.business_profession.presumptive.applicable_cap || 0)}</div>
                  </>
                )}
                {comp.presumptive_scheme === "44ADA" && (
                  <>
                    <div className="text-gray-600">Gross Receipts</div>
                    <div className="text-right font-mono">₹ {formatINR(computed.heads.business_profession.presumptive.gross_receipts || 0)}</div>
                    <div className="text-gray-600">Applicable Cap (₹75L high)</div>
                    <div className="text-right font-mono">₹ {formatINR(computed.heads.business_profession.presumptive.applicable_cap || 0)}</div>
                  </>
                )}
              </div>
            )}
          </SectionCard>

          {/* Head 4: Capital Gains */}
          <SectionCard title="Head 4 — Capital Gains" subtitle="Special-rate CG buckets are taxed independently; residual STCG/LTCG can be added to slab-rated income." testid="sec-capital-gains">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <AmountInput label="STCG u/s 111A (equity STT) — @ 20%" value={comp.stcg_111a} onChange={(v) => setComp({ ...comp, stcg_111a: v })} testid="input-stcg-111a" />
              <AmountInput label="LTCG u/s 112A (equity STT, gross) — @ 12.5% > ₹1.25L" value={comp.ltcg_112a} onChange={(v) => setComp({ ...comp, ltcg_112a: v })} testid="input-ltcg-112a" />
              <AmountInput label="LTCG — Other assets — @ 12.5%" value={comp.ltcg_other} onChange={(v) => setComp({ ...comp, ltcg_other: v })} />
              <AmountInput label="STCG — Other (slab-rated)" value={comp.stcg_normal} onChange={(v) => setComp({ ...comp, stcg_normal: v })} />
              <AmountInput label="Residual CG at slab (manual)" value={comp.capital_gains.manual ?? ""} onChange={(v) => setComp({ ...comp, capital_gains: { ...comp.capital_gains, manual: v || null } })} />
            </div>
          </SectionCard>

          {/* Head 5: Other Sources — detailed break-up (always visible) */}
          <SectionCard
            title="Head 5 — Income from Other Sources"
            subtitle="Interest, dividends, family pension, gifts, winnings (Sec 115BB) — everything under Section 56."
            testid="sec-other-sources"
          >
            <div className="space-y-4">
              {/* Interest */}
              <div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-600 mb-2">Interest income</div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <AmountInput label="Savings-bank interest" value={comp.other_sources_detail.interest_savings_bank} onChange={(v) => setComp({ ...comp, other_sources_detail: { ...comp.other_sources_detail, interest_savings_bank: v } })} testid="input-int-sb" />
                    <AmountInput label="FD / RD interest" value={comp.other_sources_detail.interest_fd_rd} onChange={(v) => setComp({ ...comp, other_sources_detail: { ...comp.other_sources_detail, interest_fd_rd: v } })} testid="input-int-fd" />
                    <AmountInput label="Other interest (bonds, PF, etc.)" value={comp.other_sources_detail.interest_others} onChange={(v) => setComp({ ...comp, other_sources_detail: { ...comp.other_sources_detail, interest_others: v } })} />
                  </div>
                </div>
                {/* Dividend */}
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-600 mb-2">Dividend income (taxable in hands of recipient)</div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <AmountInput label="Domestic company" value={comp.other_sources_detail.dividend_domestic} onChange={(v) => setComp({ ...comp, other_sources_detail: { ...comp.other_sources_detail, dividend_domestic: v } })} testid="input-div-dom" />
                    <AmountInput label="Mutual funds" value={comp.other_sources_detail.dividend_mutual_funds} onChange={(v) => setComp({ ...comp, other_sources_detail: { ...comp.other_sources_detail, dividend_mutual_funds: v } })} />
                    <AmountInput label="Foreign" value={comp.other_sources_detail.dividend_foreign} onChange={(v) => setComp({ ...comp, other_sources_detail: { ...comp.other_sources_detail, dividend_foreign: v } })} />
                  </div>
                </div>
                {/* Family pension + rental */}
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-600 mb-2">Other slab-taxed receipts</div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <AmountInput label="Family pension (gross)" value={comp.other_sources_detail.family_pension} onChange={(v) => setComp({ ...comp, other_sources_detail: { ...comp.other_sources_detail, family_pension: v } })} testid="input-family-pension" />
                    <AmountInput label="Rental — machinery/furniture" value={comp.other_sources_detail.rental_mach_furniture} onChange={(v) => setComp({ ...comp, other_sources_detail: { ...comp.other_sources_detail, rental_mach_furniture: v } })} />
                    <AmountInput label="Others (slab-taxed)" value={comp.other_sources_detail.others_slab} onChange={(v) => setComp({ ...comp, other_sources_detail: { ...comp.other_sources_detail, others_slab: v } })} />
                  </div>
                </div>
                {/* Gifts */}
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-600 mb-2">Gifts &amp; casual receipts</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <AmountInput label="Gifts received (aggregate)" value={comp.other_sources_detail.gifts_received} onChange={(v) => setComp({ ...comp, other_sources_detail: { ...comp.other_sources_detail, gifts_received: v } })} testid="input-gifts" />
                    <div className="text-[11px] text-gray-500 flex items-end pb-2">
                      Sec 56(2)(x): Only taxed if aggregate &gt; <strong>₹50,000</strong>. Then <em>entire</em> amount is taxable.
                    </div>
                  </div>
                </div>
                {/* Winnings — 115BB */}
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-amber-700 mb-2">Winnings — Sec 115BB (flat 30%, NO deductions / NO rebate)</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <AmountInput label="Lottery / races / TV shows / crossword" value={comp.other_sources_detail.winnings_lottery_games} onChange={(v) => setComp({ ...comp, other_sources_detail: { ...comp.other_sources_detail, winnings_lottery_games: v } })} testid="input-winnings" />
                    <div className="text-[11px] text-gray-500 flex items-end pb-2">
                      Taxed separately at 30% (routed to a special bucket). Not part of slab tax.
                    </div>
                  </div>
                </div>
                {/* Section 57 deductions */}
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-600 mb-2">Deductions u/s 57</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <AmountInput
                      label="Family-pension deduction (auto = min ₹15k or 1/3rd)"
                      value={comp.other_sources_detail.deduction_family_pension ?? ""}
                      onChange={(v) => setComp({ ...comp, other_sources_detail: { ...comp.other_sources_detail, deduction_family_pension: v === "" ? null : v } })}
                    />
                    <AmountInput label="Other Sec-57 deductions" value={comp.other_sources_detail.deduction_others} onChange={(v) => setComp({ ...comp, other_sources_detail: { ...comp.other_sources_detail, deduction_others: v } })} />
                  </div>
                </div>
                {/* Computed break-up preview */}
                {computed?.heads?.other_sources?.detail && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs" data-testid="os-computed-preview">
                    <div className="font-medium text-emerald-800 mb-1">Head 5 result</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <ComputedCell label="Slab-taxed (gross)" value={computed.heads.other_sources.detail.slab_gross} />
                      <ComputedCell label="Sec-57 deduction" value={computed.heads.other_sources.detail.section_57_ded} />
                      <ComputedCell label="Slab-taxed (net)" value={computed.heads.other_sources.detail.slab_net} highlight />
                      <ComputedCell label="115BB winnings" value={computed.heads.other_sources.detail.winnings_115bb} amber />
                    </div>
                  </div>
                )}
                {/* 80TTA/TTB hint */}
                {computed?.ttb_hint?.applicable && (
                  <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs flex items-start gap-2" data-testid="ttb-hint-banner">
                    <span className="text-blue-700 font-bold">💡</span>
                    <div>
                      <strong>{computed.ttb_hint.section} eligible:</strong> {computed.ttb_hint.reason}{" "}
                      Add up to <strong>₹{formatINR(computed.ttb_hint.eligible_deduction)}</strong> in Chapter VI-A.
                    </div>
                  </div>
                )}
              </div>
          </SectionCard>

          {/* Agriculture Income & Clubbing shortcut cards */}
          {(comp.agriculture_income > 0 || Object.keys(comp.clubbing_by_head || {}).length > 0) && (
            <SectionCard title="Agriculture Income · Clubbing" subtitle="Pushed from the /tax/agri-income and /tax/clubbing modules." testid="sec-agri-clubbing">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="border border-lime-200 bg-lime-50 rounded p-3">
                  <div className="text-[11px] uppercase tracking-wider text-lime-800">Agricultural income</div>
                  <div className="text-lg font-mono">₹ {formatINR(comp.agriculture_income || 0)}</div>
                  {computed?.agriculture && (
                    <div className="text-[11px] text-lime-800 mt-1">
                      {computed.agriculture.applicable ? "✓ Partial integration applied" : `⚠ Not applied — ${computed.agriculture.reason}`}
                    </div>
                  )}
                  <a href="/tax/agri-income" className="text-[11px] text-blue-700 hover:underline" data-testid="link-agri-module">Manage rows →</a>
                </div>
                <div className="border border-fuchsia-200 bg-fuchsia-50 rounded p-3">
                  <div className="text-[11px] uppercase tracking-wider text-fuchsia-800">Clubbing (Sec 60-64)</div>
                  {Object.keys(comp.clubbing_by_head || {}).length === 0 ? (
                    <div className="text-xs text-gray-500">No clubbing pushed</div>
                  ) : (
                    <div className="text-xs space-y-0.5">
                      {Object.entries(comp.clubbing_by_head || {}).map(([h, v]) => (
                        <div key={h} className="flex justify-between">
                          <span>{h.replace("_", " ")}</span>
                          <span className="font-mono">₹ {formatINR(v)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <a href="/tax/clubbing" className="text-[11px] text-blue-700 hover:underline" data-testid="link-clubbing-module">Manage rows →</a>
                </div>
              </div>
            </SectionCard>
          )}

          {/* Set-off */}
          <SectionCard title="Set-off of Losses" subtitle="Current-year inter-head set-off is manual; brought-forward set-off is auto-managed from the Loss ledger." testid="sec-setoff">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <AmountInput label="Current year set-off" value={comp.setoff_current_year} onChange={(v) => setComp({ ...comp, setoff_current_year: v })} testid="input-setoff-current" />
              <div className="space-y-1" data-testid="setoff-bf-auto">
                <div className="text-xs text-gray-600 flex items-center justify-between">
                  <span>Brought-forward set-off <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium ml-1">Auto</span></span>
                  <button type="button" onClick={() => navigate("/tax/losses")} className="text-[11px] text-blue-700 hover:underline" data-testid="link-loss-ledger">Loss ledger →</button>
                </div>
                <div className="h-9 flex items-center px-3 rounded border border-gray-200 bg-gray-50 font-mono text-sm text-gray-800" data-testid="setoff-bf-value">₹ {formatINR(comp.setoff_brought_forward || 0)}</div>
                <p className="text-[10px] text-gray-400">Set off automatically from prior-year Business / House-Property losses when you save.</p>
              </div>
            </div>
          </SectionCard>

          {/* Chapter VI-A */}
          <SectionCard
            title="Chapter VI-A Deductions"
            subtitle={`Regime: ${comp.profile_snapshot.tax_regime}. Under New Regime, only 80CCD(2) & 80JJAA are allowed.`}
            testid="sec-chapter-via"
            right={
              <div className="flex items-center gap-2">
                <select
                  onChange={(e) => { addViaRow(e.target.value); e.target.value = ""; }}
                  className="h-8 text-xs border border-gray-300 rounded px-2 bg-white"
                  data-testid="select-add-via"
                >
                  <option value="">+ Add section</option>
                  {availableViaOptions.map((s) => (
                    <option key={s.code} value={s.code}>{s.code} — {s.title}</option>
                  ))}
                </select>
              </div>
            }
          >
            {(comp.chapter_via || []).length === 0 && <div className="text-xs text-gray-400 italic">No deductions claimed</div>}
            <table className="tx-table w-full">
              <thead>
                <tr>
                  <th className="w-24">Section</th>
                  <th>Description</th>
                  <th className="text-right w-32">Claimed</th>
                  <th className="text-right w-32">Allowed</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {(comp.chapter_via || []).map((r, idx) => {
                  const computedRow = (computed?.chapter_via?.rows || []).find((cr) => cr.code === r.code);
                  return (
                    <tr key={r.code}>
                      <td className="font-mono text-xs font-semibold">{r.code}</td>
                      <td className="text-xs">{r.title || computedRow?.title || r.code}</td>
                      <td>
                        <Input type="number" value={r.claimed} onChange={(e) => updateVia(idx, { claimed: Number(e.target.value) })} className="h-8 text-right font-mono text-xs" data-testid={`input-via-claimed-${r.code}`} />
                      </td>
                      <td className="text-right font-mono text-xs bg-emerald-50">{formatINR(computedRow?.allowed ?? 0)}</td>
                      <td>
                        <button onClick={() => removeVia(idx)} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </SectionCard>

          {/* Taxes Paid */}
          <SectionCard
            title="Taxes Paid"
            subtitle="TDS/TCS, Advance Tax, Self-Assessment Tax."
            testid="sec-taxes-paid"
            right={
              <label className="text-[11px] flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(comp.tds_rows || []).length > 0}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setComp({ ...comp, tds_rows: [{ section: "192", deductor_name: "", deductor_tan: "", amount: 0, period: "", notes: "" }] });
                    } else if (window.confirm("Discard all TDS ledger rows?")) {
                      setComp({ ...comp, tds_rows: [] });
                    }
                  }}
                  data-testid="check-tds-ledger"
                />
                Use TDS ledger
              </label>
            }
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <AmountInput label="TDS" value={comp.tds} onChange={(v) => setComp({ ...comp, tds: v })} testid="input-tds" />
              <AmountInput label="TCS" value={comp.tcs} onChange={(v) => setComp({ ...comp, tcs: v })} />
              <AmountInput label="Advance Tax" value={comp.advance_tax} onChange={(v) => setComp({ ...comp, advance_tax: v })} testid="input-advance-tax" />
              <AmountInput label="Self-Assessment Tax" value={comp.self_assessment_tax} onChange={(v) => setComp({ ...comp, self_assessment_tax: v })} />
            </div>

            {(comp.tds_rows || []).length > 0 && (
              <div className="mt-4 border border-blue-200 bg-blue-50/40 rounded p-3" data-testid="tds-ledger">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-wider text-blue-800 font-medium">
                    TDS Ledger — row totals OVERRIDE the TDS field above
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-[11px]"
                    onClick={() => setComp({
                      ...comp,
                      tds_rows: [...(comp.tds_rows || []),
                                 { section: "", deductor_name: "", deductor_tan: "", amount: 0, period: "", notes: "" }],
                    })}
                    data-testid="btn-add-tds-row"
                  >
                    + Add row
                  </Button>
                </div>
                <table className="tx-table w-full">
                  <thead>
                    <tr>
                      <th className="w-20">Section</th>
                      <th>Deductor</th>
                      <th className="w-32">TAN</th>
                      <th className="w-24">Period</th>
                      <th className="text-right w-28">Amount</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(comp.tds_rows || []).map((r, idx) => (
                      <tr key={idx}>
                        <td>
                          <Input value={r.section || ""} onChange={(e) => {
                            const rows = [...comp.tds_rows]; rows[idx] = { ...r, section: e.target.value }; setComp({ ...comp, tds_rows: rows });
                          }} className="h-7 text-xs" placeholder="192" data-testid={`input-tds-section-${idx}`} />
                        </td>
                        <td>
                          <Input value={r.deductor_name || ""} onChange={(e) => {
                            const rows = [...comp.tds_rows]; rows[idx] = { ...r, deductor_name: e.target.value }; setComp({ ...comp, tds_rows: rows });
                          }} className="h-7 text-xs" placeholder="Deductor name" data-testid={`input-tds-deductor-${idx}`} />
                        </td>
                        <td>
                          <Input value={r.deductor_tan || ""} onChange={(e) => {
                            const rows = [...comp.tds_rows]; rows[idx] = { ...r, deductor_tan: e.target.value }; setComp({ ...comp, tds_rows: rows });
                          }} className="h-7 text-xs font-mono" placeholder="TAN" />
                        </td>
                        <td>
                          <Input value={r.period || ""} onChange={(e) => {
                            const rows = [...comp.tds_rows]; rows[idx] = { ...r, period: e.target.value }; setComp({ ...comp, tds_rows: rows });
                          }} className="h-7 text-xs" placeholder="Q1-FY26" />
                        </td>
                        <td>
                          <Input type="number" value={r.amount ?? 0} onChange={(e) => {
                            const rows = [...comp.tds_rows]; rows[idx] = { ...r, amount: parseFloat(e.target.value) || 0 }; setComp({ ...comp, tds_rows: rows });
                          }} className="h-7 text-right font-mono text-xs" data-testid={`input-tds-amount-${idx}`} />
                        </td>
                        <td>
                          <button onClick={() => setComp({ ...comp, tds_rows: comp.tds_rows.filter((_, i) => i !== idx) })} className="text-red-600" data-testid={`btn-tds-remove-${idx}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-2 text-[11px] text-blue-800 flex justify-between">
                  <span>{(comp.tds_rows || []).length} row(s) — total will override the TDS field.</span>
                  <span className="font-mono">
                    Total: ₹ {formatINR((comp.tds_rows || []).reduce((s, r) => s + (Number(r.amount) || 0), 0))}
                  </span>
                </div>
              </div>
            )}
          </SectionCard>

          {/* Regime Comparator */}
          {computed?.regime_comparison && (
            <SectionCard
              title="Old vs New Regime — Side-by-side"
              subtitle="Live comparison. Switch the taxpayer's regime under Profile to lock in the recommendation."
              testid="sec-regime-comparator"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <RegimeCard label="Current" data={computed.regime_comparison.current} highlight={computed.regime_comparison.recommendation === computed.regime_comparison.current.regime} />
                <RegimeCard label="Alternate" data={computed.regime_comparison.alternate} highlight={computed.regime_comparison.recommendation === computed.regime_comparison.alternate.regime} />
              </div>
              {computed.regime_comparison.savings_if_switched > 0 && (
                <div className="mt-3 text-xs bg-emerald-50 border border-emerald-200 rounded p-2 text-emerald-900" data-testid="regime-savings-hint">
                  Switching to <strong>{computed.regime_comparison.recommendation}</strong> regime could save you
                  <strong> ₹{formatINR(computed.regime_comparison.savings_if_switched)}</strong> this year.
                </div>
              )}
            </SectionCard>
          )}

          {/* Interest 234A / 234B / 234C — Auto-computed */}
          <SectionCard
            title="Interest u/s 234A · 234B · 234C"
            subtitle="Auto-computed by the engine. Click an input to override any value manually."
            testid="sec-interest"
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
              <div className="space-y-1">
                <Label className="text-[11px] uppercase tracking-wider text-gray-600">Return Due Date</Label>
                <Input
                  type="date"
                  value={comp.return_due_date || ""}
                  onChange={(e) => setComp({ ...comp, return_due_date: e.target.value })}
                  className="h-9 text-xs"
                  data-testid="input-return-due-date"
                />
                <p className="text-[10px] text-gray-500">Non-audit: 31-Jul, Audit: 31-Oct</p>
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] uppercase tracking-wider text-gray-600">Return Filing Date</Label>
                <Input
                  type="date"
                  value={comp.return_filing_date || ""}
                  onChange={(e) => setComp({ ...comp, return_filing_date: e.target.value })}
                  className="h-9 text-xs"
                  data-testid="input-return-filing-date"
                />
                <p className="text-[10px] text-gray-500">Leave blank if not yet filed</p>
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] uppercase tracking-wider text-gray-600">As-of Date</Label>
                <Input
                  type="date"
                  value={comp.computation_as_of_date || ""}
                  onChange={(e) => setComp({ ...comp, computation_as_of_date: e.target.value })}
                  className="h-9 text-xs"
                  data-testid="input-computation-as-of-date"
                />
                <p className="text-[10px] text-gray-500">Interest runs until this date if unfiled. Blank = today.</p>
              </div>
              <label className="flex items-start gap-2 pt-6">
                <input
                  type="checkbox"
                  checked={!!comp.is_audit_case}
                  onChange={(e) => {
                    const audit = e.target.checked;
                    setComp({ ...comp, is_audit_case: audit, return_due_date: audit ? "2026-10-31" : "2026-07-31" });
                  }}
                  className="mt-0.5"
                  data-testid="check-audit-case"
                />
                <div className="text-xs">
                  <div className="font-medium">Audit case</div>
                  <div className="text-[10px] text-gray-500">Sets due date to 31-Oct</div>
                </div>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {["234a", "234b", "234c"].map((sec) => {
                const key = `interest_${sec}`;
                const auto = computed?.interest?.[`${sec}_auto`] ?? 0;
                const manual = comp[key];
                const isOverridden = manual !== null && manual !== undefined;
                const displayVal = isOverridden ? manual : auto;
                return (
                  <div key={sec} className="space-y-1 border border-gray-200 rounded p-3" data-testid={`interest-block-${sec}`}>
                    <div className="flex items-center justify-between">
                      <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">
                        {`Interest u/s ${sec.toUpperCase()}`}
                      </Label>
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded-full uppercase tracking-wider ${
                          isOverridden ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"
                        }`}
                        data-testid={`interest-badge-${sec}`}
                      >
                        {isOverridden ? "Manual" : "Auto"}
                      </span>
                    </div>
                    <Input
                      type="number"
                      value={displayVal === 0 && !isOverridden ? "" : displayVal ?? ""}
                      placeholder={`Auto: ₹${formatINR(auto)}`}
                      onChange={(e) => {
                        const v = e.target.value;
                        setComp({ ...comp, [key]: v === "" ? 0 : Number(v) });
                      }}
                      className={`text-right font-mono h-9 tabular-nums ${isOverridden ? "bg-amber-50/50" : ""}`}
                      data-testid={`input-${key.replace("_", "-")}`}
                    />
                    <div className="flex items-center justify-between text-[10px] text-gray-500">
                      <span>Auto: ₹ {formatINR(auto)}</span>
                      {isOverridden && (
                        <button
                          onClick={() => setComp({ ...comp, [key]: null })}
                          className="text-blue-700 hover:underline"
                          data-testid={`btn-reset-${sec}`}
                        >
                          Reset to auto
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-3 text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded p-2 leading-relaxed">
              <strong>How it&apos;s computed:</strong> <br />
              <strong>234A</strong>: 1%/month × months of delay (Filing / As-of date − Due date) × unpaid tax (Total Tax − TDS − TCS − Advance − SAT). Runs even if the return is not yet filed — interest accrues until the return is furnished or an assessment is completed.<br />
              <strong>234B</strong>: 1%/month × months from 1-Apr of AY to Filing / As-of date × (Assessed Tax − Advance Tax), only if Advance Tax {"<"} 90% of Assessed Tax.<br />
              <strong>234C</strong>: Standard installment schedule (15/45/75/100%); assumes advance tax was paid evenly across 4 installments. Presumptive taxpayers = 1 installment @ 100% by 15-Mar.
            </div>
          </SectionCard>

          {/* Penalty u/s 234F — Late-filing fee */}
          <SectionCard
            title="Penalty u/s 234F · Late-Filing Fee"
            subtitle="Auto-computed when the return is filed after the due date (or is unfiled past the due date)."
            testid="sec-penalty"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="border border-gray-200 rounded p-3 bg-gray-50">
                <div className="text-[11px] uppercase tracking-wider text-gray-600">Auto</div>
                <div className="text-lg font-mono">₹ {formatINR(computed?.penalty?.["234f_auto"] || 0)}</div>
                {computed?.penalty?.["234f_reason"] && (
                  <p className="text-[10px] text-gray-500 mt-1">{computed.penalty["234f_reason"]}</p>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] uppercase tracking-wider text-gray-600">Manual override (leave blank for auto)</Label>
                <Input
                  type="number"
                  value={comp.late_filing_fee_234f ?? ""}
                  onChange={(e) => setComp({ ...comp, late_filing_fee_234f: e.target.value === "" ? null : parseFloat(e.target.value) })}
                  placeholder="Leave blank for auto"
                  className="h-9 text-xs"
                  data-testid="input-234f"
                />
                {computed?.penalty?.["234f"] > 0 && (
                  <div className="text-[11px] text-rose-700 mt-1">
                    Payable: <strong>₹{formatINR(computed.penalty["234f"])}</strong>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-3 text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded p-2 leading-relaxed">
              <strong>Section 234F</strong> (Finance Act 2017, amended by FA 2021): Late-filing fee is
              <strong> ₹1,000</strong> if total income ≤ ₹5,00,000, else <strong>₹5,000</strong>. If total income is at or below the basic exemption limit, the return isn&apos;t mandatory and no fee applies.
            </div>
          </SectionCard>

          <SectionCard title="Notes" subtitle="Any working-paper notes for reference." testid="sec-notes">
            <Textarea value={comp.notes} onChange={(e) => setComp({ ...comp, notes: e.target.value })} rows={3} placeholder="Assumptions, references, follow-ups…" />
          </SectionCard>
        </div>

        {/* RIGHT: Live computed summary */}
        <div className="lg:col-span-1">
          <div className="lg:sticky lg:top-16 space-y-3">
            <div className="border border-gray-200 bg-white rounded" data-testid="tax-summary-card">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-semibold text-sm">Computation Summary {previewing && <RefreshCw className="w-3 h-3 inline animate-spin text-gray-400 ml-1" />}</h3>
                <button onClick={() => preview(comp)} className="text-xs text-blue-700 flex items-center gap-1 hover:underline" data-testid="btn-preview">
                  <Play className="w-3 h-3" /> Recompute
                </button>
              </div>
              {computed ? (
                <div className="p-4 space-y-3 text-xs">
                  <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                    <div className="text-gray-600">Salary (net)</div>
                    <div className="tx-num font-mono">₹ {formatINR(computed.heads?.salary?.net || 0)}</div>
                    <div className="text-gray-600">House Property</div>
                    <div className="tx-num font-mono">₹ {formatINR(computed.heads?.house_property?.net || 0)}</div>
                    <div className="text-gray-600">Business / Profession</div>
                    <div className="tx-num font-mono">₹ {formatINR(computed.heads?.business_profession?.net || 0)}</div>
                    <div className="text-gray-600">Capital Gains</div>
                    <div className="tx-num font-mono">₹ {formatINR(computed.heads?.capital_gains?.net || 0)}</div>
                    <div className="text-gray-600">Other Sources</div>
                    <div className="tx-num font-mono">₹ {formatINR(computed.heads?.other_sources?.net || 0)}</div>
                  </div>
                  <div className="border-t border-gray-100 pt-2 grid grid-cols-2 gap-x-3 gap-y-1">
                    <div className="text-gray-700 font-medium">Gross Total Income</div>
                    <div className="tx-num font-mono font-medium" data-testid="summary-gti">₹ {formatINR(computed.gross_total_income)}</div>
                    <div className="text-gray-600">Less: Set-off</div>
                    <div className="tx-num font-mono text-red-700">− ₹ {formatINR(computed.setoff?.total || 0)}</div>
                    <div className="text-gray-600">Less: Chapter VI-A</div>
                    <div className="tx-num font-mono text-red-700">− ₹ {formatINR(computed.chapter_via?.total || 0)}</div>
                  </div>
                  <div className="border-t border-gray-100 pt-2 bg-blue-50 -mx-4 -mb-4 px-4 py-3 space-y-1.5">
                    <div className="flex justify-between text-sm font-semibold">
                      <span>Total Income</span>
                      <span className="font-mono" data-testid="summary-total-income">₹ {formatINR(computed.total_income)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Tax before rebate</span>
                      <span className="font-mono">₹ {formatINR(computed.tax?.before_rebate || 0)}</span>
                    </div>
                    {(computed.tax?.rebate_87a || 0) > 0 && (
                      <div className="flex justify-between text-xs text-emerald-700">
                        <span>Less: Rebate 87A</span>
                        <span className="font-mono">− ₹ {formatINR(computed.tax.rebate_87a)}</span>
                      </div>
                    )}
                    {(computed.tax?.surcharge || 0) > 0 && (
                      <div className="flex justify-between text-xs">
                        <span>Add: Surcharge</span>
                        <span className="font-mono">+ ₹ {formatINR(computed.tax.surcharge)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xs">
                      <span>Add: Cess 4%</span>
                      <span className="font-mono">+ ₹ {formatINR(computed.tax?.cess || 0)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-bold border-t border-blue-200 pt-1.5">
                      <span>Total Tax</span>
                      <span className="font-mono" data-testid="summary-total-tax">₹ {formatINR(computed.tax?.total || 0)}</span>
                    </div>
                    {(computed.interest?.total || 0) > 0 && (
                      <div className="flex justify-between text-xs">
                        <span>Add: Interest 234A/B/C</span>
                        <span className="font-mono">+ ₹ {formatINR(computed.interest.total)}</span>
                      </div>
                    )}
                    {(computed.penalty?.total || 0) > 0 && (
                      <div className="flex justify-between text-xs" data-testid="summary-penalty-234f">
                        <span>Add: Penalty u/s 234F</span>
                        <span className="font-mono">+ ₹ {formatINR(computed.penalty.total)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xs">
                      <span>Less: Tax paid (TDS/Adv/SA)</span>
                      <span className="font-mono text-red-700">− ₹ {formatINR(computed.tax_paid?.total || 0)}</span>
                    </div>
                    <div className={`flex justify-between text-sm font-bold border-t border-blue-200 pt-1.5 ${computed.refund_or_payable > 0 ? "text-red-700" : computed.refund_or_payable < 0 ? "text-emerald-700" : ""}`}>
                      <span>{computed.refund_or_payable > 0 ? "Balance Payable" : computed.refund_or_payable < 0 ? "Refund Due" : "Nil"}</span>
                      <span className="font-mono" data-testid="summary-refund-payable">₹ {formatINR(Math.abs(computed.refund_or_payable || 0))}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 text-xs text-gray-500">Enter data to see live computation.</div>
              )}
            </div>

            {/* Regime warning */}
            {comp.profile_snapshot.tax_regime === "New" && (comp.chapter_via || []).length > 0 && (
              <div className="border border-amber-200 bg-amber-50 rounded p-3 text-xs text-amber-900">
                <strong>Note:</strong> Under the New Regime, most Chapter VI-A deductions won&apos;t be allowed. Only 80CCD(2) is disallowed-safe. The &ldquo;Allowed&rdquo; column will reflect the actual allowance.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* AIS Import Modal — extract TDS / dividend / STCG / LTCG etc. from
          the Annual Information Statement PDF via Gemini 3 Pro, review, then
          apply. Every field remains editable after apply. */}
      <Dialog open={aisOpen} onOpenChange={(o) => { if (!o) { setAisOpen(false); setAisFile(null); setAisResult(null); } }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-hidden flex flex-col p-0" data-testid="ais-import-dialog">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-gray-100">
            <DialogTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-600" />
              Import from AIS (Annual Information Statement)
            </DialogTitle>
            <p className="text-xs text-gray-500 mt-1">
              Upload the client&apos;s AIS PDF downloaded from the Income-Tax portal.
              AI (Gemini 3 Pro) reads it and fills TDS, dividend, interest,
              STCG / LTCG etc. into the computation — you can still edit anything.
            </p>
          </DialogHeader>
          <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
            {aisBusy && aisProgress && (
              <div className="border border-emerald-200 bg-emerald-50 rounded p-3" data-testid="ais-progress-card">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2 text-sm text-emerald-900 font-medium">
                    <Sparkles className="w-4 h-4 animate-pulse" />
                    <span data-testid="ais-progress-message">{aisProgress.message || "Working…"}</span>
                  </div>
                  <div className="text-xs font-mono text-emerald-700">
                    {aisProgress.pct || 0}%
                  </div>
                </div>
                <div className="h-1.5 bg-white border border-emerald-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-600 transition-all duration-500 ease-out"
                    style={{ width: `${aisProgress.pct || 0}%` }}
                  />
                </div>
                <div className="text-[11px] text-emerald-800 mt-2">
                  Gemini 3 Pro is reading the PDF. Multi-page AIS files usually finish in 15-45 seconds.
                </div>
              </div>
            )}
            {!aisBusy && !aisResult && (
              <>
                <label className="flex items-center justify-center border-2 border-dashed border-emerald-300 bg-emerald-50/40 rounded-lg p-8 text-sm text-emerald-700 cursor-pointer hover:border-emerald-500 hover:bg-emerald-50">
                  <input
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => setAisFile(e.target.files?.[0] || null)}
                    data-testid="input-ais-file"
                  />
                  <div className="text-center">
                    <FileUp className="w-10 h-10 mx-auto mb-2 text-emerald-600" />
                    <div className="font-medium">{aisFile ? aisFile.name : "Click to choose AIS PDF"}</div>
                    <div className="text-[11px] text-gray-500 mt-1">≤ 10 MB · downloaded from incometax.gov.in</div>
                  </div>
                </label>
                <div className="text-[11px] text-gray-500 leading-relaxed">
                  <strong className="text-gray-700">Where to find the AIS:</strong> Login at
                  <a className="text-blue-700 underline mx-1" href="https://www.incometax.gov.in" target="_blank" rel="noreferrer">
                    incometax.gov.in
                  </a>
                  → <em>e-File → Income Tax Returns → View AIS</em> → <em>Download</em> (choose PDF, password = your PAN in lowercase + DOB DDMMYYYY). Remove the password before uploading.
                </div>
              </>
            )}
            {!aisBusy && aisResult && aisResult.summary && (
              <>
                {/* Extracted-data review card */}
                <div className="border border-emerald-200 bg-emerald-50/50 rounded p-3 space-y-2" data-testid="ais-summary-card">
                  <div className="text-xs font-semibold uppercase tracking-wider text-emerald-800">
                    Extracted from AIS
                  </div>
                  {aisResult.summary.taxpayer_pan && (
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">PAN</span>
                      <span className="font-mono">{aisResult.summary.taxpayer_pan}</span>
                    </div>
                  )}
                  {aisResult.summary.taxpayer_name && (
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Name</span>
                      <span>{aisResult.summary.taxpayer_name}</span>
                    </div>
                  )}
                  {aisResult.summary.fy_reported && (
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">FY</span>
                      <span>{aisResult.summary.fy_reported}</span>
                    </div>
                  )}
                  <div className="border-t border-emerald-200 pt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    {[
                      ["Salary (gross)", aisResult.summary.totals.salary, "amt-salary"],
                      ["Interest income", aisResult.summary.totals.interest, "amt-interest"],
                      ["Dividend income", aisResult.summary.totals.dividend, "amt-dividend"],
                      ["Rent received", aisResult.summary.totals.rent, "amt-rent"],
                      ["STCG (equity, 111A)", aisResult.summary.totals.stcg_111a, "amt-stcg111a"],
                      ["LTCG (equity, 112A)", aisResult.summary.totals.ltcg_112a, "amt-ltcg112a"],
                      ["STCG (other, slab)", aisResult.summary.totals.stcg_normal, "amt-stcg-normal"],
                      ["LTCG (other, 12.5%)", aisResult.summary.totals.ltcg_other, "amt-ltcg-other"],
                      ["TDS deducted", aisResult.summary.totals.tds, "amt-tds"],
                      ["TCS collected", aisResult.summary.totals.tcs, "amt-tcs"],
                      ["Advance tax paid", aisResult.summary.totals.advance_tax, "amt-adv"],
                      ["Self-assessment tax", aisResult.summary.totals.self_assessment_tax, "amt-sat"],
                    ].filter(([, v]) => (v || 0) > 0).map(([label, v, tid]) => (
                      <div key={tid} className="contents">
                        <span className="text-gray-700">{label}</span>
                        <span className="text-right font-mono tabular-nums" data-testid={`ais-${tid}`}>₹ {formatINR(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {(aisResult.warnings || []).length > 0 && (
                  <div className="border border-amber-200 bg-amber-50 rounded p-3 text-xs text-amber-900 space-y-1" data-testid="ais-warnings">
                    <div className="font-semibold">Please review:</div>
                    <ul className="list-disc pl-5">
                      {aisResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </div>
                )}
                <div className="text-[11px] text-gray-500">
                  Clicking Apply will fill the extracted values into the computation.
                  Every field remains editable — verify against the source AIS and adjust as needed.
                </div>
              </>
            )}
          </div>
          <DialogFooter className="px-6 py-3 border-t border-gray-100 bg-white">
            <Button variant="outline" onClick={() => { setAisOpen(false); setAisFile(null); setAisResult(null); }} data-testid="btn-ais-cancel">
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            {!aisResult ? (
              <Button
                onClick={uploadAIS}
                disabled={!aisFile || aisBusy}
                className="bg-emerald-700 hover:bg-emerald-800"
                data-testid="btn-ais-extract"
              >
                {aisBusy
                  ? <><Sparkles className="w-4 h-4 mr-1 animate-pulse" /> Extracting…</>
                  : <><Sparkles className="w-4 h-4 mr-1" /> Extract with AI</>}
              </Button>
            ) : (
              <Button
                onClick={applyAIS}
                className="bg-emerald-700 hover:bg-emerald-800"
                data-testid="btn-ais-apply"
              >
                <Save className="w-4 h-4 mr-1" /> Apply to Computation
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function TaxComputation() {
  return <RequireCompany><Inner /></RequireCompany>;
}
